import { useEffect, useState } from "react";
import { useCurrentUser } from "../../../../providers/CurrentUserProvider";
import { useHistory, useParams } from "react-router-dom";
import { getRegistrations } from "../../../../../actions/athlete";
import { RegistrationWidget } from "../../../../layout/registration/RegistrationWidget";
import { register } from "../../../../../actions/event";
import { useDispatch } from "react-redux";
import { GET_EVENT } from "../../../../../graphql/event";
import { useApolloClient } from "@apollo/client";
import { Container } from "@mui/material";
import { useCreateCheckoutSession } from "../../../../../hooks/useCreateCheckoutSession";
import { useLocalizationContext } from "../../../../providers/LocalizationProvider";
import { EventDivision } from "models/EventDivision";
import { useEventChannel } from "../EventProvider";
import { useRefocusEffect } from "hooks/useRefocusEffect";
import { isEqual } from "lodash";
import { useBindToChannelEvent } from "../../../../providers/Realtime/RealtimeProvider";

const useRegister = (id, loadRegistrations) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const client = useApolloClient();
    return values =>
        dispatch(register(id, values))
            .then(() => history.push(`/events/${id}/registration/success`, { registrationSuccessful: true }))
            .catch(e => {
                if (e?.response?.status === 422) {
                    loadRegistrations();
                    client.query({ query: GET_EVENT, variables: { id }, fetchPolicy: "network-only" });
                }
            });
};

export const EventRegistrationWrapper = ({ event }) => {
    const { id } = useParams();
    const { currentUser } = useCurrentUser();
    const [athletes, setAthletes] = useState();
    const loadRegistrations = () => {
        if (currentUser)
            getRegistrations(id).then(response => {
                if (!isEqual(athletes, response.data.athletes)) setAthletes(response.data.athletes);
            });
    };
    useEffect(loadRegistrations, [currentUser]);
    useRefocusEffect(loadRegistrations, true);

    const eventChannel = useEventChannel();
    useBindToChannelEvent(eventChannel, "event-division-entries-updated", ({ id }) => event.eventDivisions.map(ed => parseInt(ed.id)).includes(id) && loadRegistrations());

    const register = useRegister(id, loadRegistrations);
    const createCheckoutSession = useCreateCheckoutSession(`payments/Event/${id}/create_checkout_session`);
    const { translate } = useLocalizationContext();

    const exclusiveSeries = event.series.filter(series => series.exclusive);

    return (
        <Container>
            <RegistrationWidget
                athletes={athletes?.map(athlete => ({
                    id: athlete.id,
                    name: athlete.name,
                    image: athlete.image,
                    dob: athlete.dob,
                    properties: athlete.properties,
                    registration_properties: athlete.registration_properties,
                    registrations_attributes: athlete.registrations.map(registration => ({
                        id: registration.id.toString(),
                        option_id: registration.option_id.toString(),
                        name: registration.name,
                        team_members: registration.team_members,
                        team_name: registration.team_name,
                    })),
                    subtext: athlete.registrations.length > 0 && translate("registered_for_x_divisions", { x: athlete.registrations.length }),
                    registeredMessage: athlete.registrations.length > 0 && { text: "already_registered_to_x", props: { x: athlete.registrations.length } },
                    exclusive_memberships: athlete.exclusive_memberships
                }))}
                options={event.eventDivisions.filter(ed => !ed.eventDivisions.length).map(ed => ({
                    optionId: ed.id,
                    name: ed.division.name,
                    spotsLeft: ed.entryLimit ? ed.entryLimit.limit - ed.entryCount : null,
                    isTeams: EventDivision(ed).isTeams(),
                    teamConfig: ed.formatDefinition?.teamConfig
                }))}
                registrationOptions={event.registrationOptions}
                captureFields={event.captureFields}
                paymentOptions={event.paymentOptions}
                register={register}
                createCheckoutSession={createCheckoutSession}
                exclusiveSeries={exclusiveSeries}
                organisation={event.organisation}
            />
        </Container>
    );
};
